import React, { useCallback, useEffect, useRef } from 'react';

// Measures the first rendered child element (matching the previous react-sizeme
// behaviour: findDOMNode -> getBoundingClientRect on the first host node) and
// reports { width, height } to the parent via setSize.
const SizeComponent = ({ children, setSize }) => {
    const wrapperRef = useRef(null);
    const lastSize = useRef({ width: -1, height: -1 });
    const rafRef = useRef(null);

    const report = useCallback(() => {
        const node = wrapperRef.current?.firstElementChild;
        if (!node || !setSize) return;

        const { width, height } = node.getBoundingClientRect();
        if (lastSize.current.width === width && lastSize.current.height === height) return;

        lastSize.current = { width, height };
        setSize({ width, height });
    }, [setSize]);

    useEffect(() => {
        const node = wrapperRef.current?.firstElementChild;
        if (!node) return undefined;

        // Initial measurement (also covers environments without ResizeObserver).
        report();

        if (typeof ResizeObserver === 'undefined') return undefined;

        const observer = new ResizeObserver(() => {
            // Defer to the next frame to avoid "ResizeObserver loop" warnings when
            // reporting the size triggers a layout change in the parent.
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
            rafRef.current = requestAnimationFrame(report);
        });
        observer.observe(node);

        return () => {
            observer.disconnect();
            if (rafRef.current) cancelAnimationFrame(rafRef.current);
        };
    }, [report, children]);

    return (
        <div ref={wrapperRef} style={{ display: 'contents' }}>
            {children}
        </div>
    );
};

export default SizeComponent;
